import { type BaseButtonProps, Button } from "@msuite/ui/button";
import { Select, Switch, TextInput } from "@msuite/ui/form";
import { useSearchForm } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { filter, guardArray, uniqueArrayByKey } from "@msuite/utils/array";
import { generateId } from "@msuite/utils/strings";
import type {
	Nullable,
	OrganizationMember,
	OrganizationMembers,
	ProjectCard,
	Tables,
} from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

/** Props Interface */
interface ProjectsFilterProps {
	controller: ProjectsFilterController;
	buttonProps?: BaseButtonProps;
}

export function ProjectsFilter({ controller, buttonProps }: ProjectsFilterProps) {
	const searchForm = useSearchForm("filter-options");
	const { t } = useTranslation();

	/** Functions */
	function handleOnToggleUser(userId: string) {
		if (controller.userIds.includes(userId)) {
			controller.setUserIds(controller.userIds.filter((id) => id !== userId));
		} else {
			controller.setUserIds([...controller.userIds, userId]);
		}
	}

	function handleOnToggleWorkspace(workspaceId: string) {
		if (controller.workspaceIds.includes(workspaceId)) {
			controller.setWorkspaceIds(controller.workspaceIds.filter((id) => id !== workspaceId));
		} else {
			controller.setWorkspaceIds([...controller.workspaceIds, workspaceId]);
		}
	}

	/** Computed */
	const hasUserFilter = controller.userIds.length > 0;
	const hasWorkspaceFilter = controller.workspaceIds.length > 0;
	const hasActiveFilters = [hasUserFilter, hasWorkspaceFilter].some((v) => v);

	/** Renderers */
	function createUserOption(member: OrganizationMember) {
		return {
			label: generateId(),
			render: () => (
				<div
					className="h-stack justify-between w-full cursor-pointer"
					onClick={() => handleOnToggleUser(member.user.id!)}
				>
					<p className="whitespace-nowrap line-clamp-1 truncate max-w-[200px]">
						{getFullName(member.user)}
					</p>
					<div className="pointer-events-none">
						<Switch
							checked={controller.userIds.includes(member.user.id!)}
							onCheckedChange={() => {}}
							id={generateId()}
						/>
					</div>
				</div>
			),
			icon: icon("Filter"),
		};
	}

	function createWorkspaceOption(workspace: Partial<Tables<"workspaces">>) {
		return {
			label: generateId(),
			render: () => (
				<div
					className="h-stack justify-between w-full cursor-pointer"
					onClick={() => handleOnToggleWorkspace(workspace.id!)}
				>
					<div className="h-stack max-w-[200px]">
						<StatusIndicator color={workspace.color || "gray"} />
						<p className="whitespace-nowrap line-clamp-1 truncate">{workspace.name}</p>
					</div>
					<div className="pointer-events-none">
						<Switch
							checked={controller.workspaceIds.includes(workspace.id!)}
							onCheckedChange={() => {}}
							id={generateId()}
						/>
					</div>
				</div>
			),
			icon: icon("Filter"),
		};
	}

	const users = controller.options.users || [];
	const workspaces = controller.options.workspaces || [];

	/** Render */
	return (
		<Menu
			items={() => [
				{
					label: "",
					render: () => (
						<TextInput
							control={searchForm.control}
							name="searchValue"
							label={undefined}
							isRequired
							type="search"
							placeholder={t("placeholders.search")}
						/>
					),
					icon: null,
				},
				"divider",
				{
					label: "",
					render: () => (
						<Select
							control={controller.modeControl}
							name="mode"
							label={undefined}
							isRequired
							isNullable={false}
							itemNotFoundLabel="No mode found"
							options={[
								{
									label: t("filter.match_any_or"),
									value: "or",
									item: { value: "or" },
								},
								{
									label: t("filter.match_all_and"),
									value: "and",
									item: { value: "and" },
								},
							]}
						/>
					),
					icon: null,
				},
				"divider",
				...filter(users, searchForm.searchValue).map(createUserOption),
				"divider",
				...filter(workspaces, searchForm.searchValue).map(createWorkspaceOption),
				"divider",
				{
					label: t("general.reset"),
					onClick: controller.reset,
					icon: icon("RotateCcw"),
				},
			]}
		>
			<div className="relative">
				<Button
					icon={<Icon name="Funnel" />}
					{...buttonProps}
				/>
				<AnimatePresence mode="popLayout">
					{hasActiveFilters && (
						<motion.div
							className="w-[7px] aspect-square absolute -top-[2px] -right-[2px] bg-rose-500 rounded-full"
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							exit={{ scale: 0 }}
							transition={{ type: "spring", stiffness: 260, damping: 20 }}
						/>
					)}
				</AnimatePresence>
			</div>
		</Menu>
	);
}

export type ProjectsFilterController = ReturnType<typeof useProjectsFilterController>;

export type ProjectsFilterInput = (Partial<Omit<ProjectCard, "createdAt" | "updatedAt">> & {
	id: string;
})[];

interface UseProjectsFilterOptions {
	users: Nullable<OrganizationMembers>;
	workspaces: Nullable<Tables<"workspaces">[]>;
	isDisabled?: boolean;
}

export function useProjectsFilterController(
	projects: Nullable<ProjectsFilterInput>,
	options: UseProjectsFilterOptions,
) {
	/** Search Form */
	const searchForm = useSearchForm();
	const modeForm = useForm<{ mode: "or" | "and" }>({
		defaultValues: { mode: "or" },
	});
	const [mode] = useWatch({ control: modeForm.control, name: ["mode"] });

	/** State */
	const [userIds, setUserIds] = useState<string[]>([]);
	const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);

	const filtered = useMemo(() => {
		if (options.isDisabled) return guardArray(projects);
		if (!searchForm.searchValue && userIds.length === 0 && workspaceIds.length === 0) {
			return guardArray(projects);
		}
		if (mode === "or") {
			return filterOr(projects || []);
		} else {
			return filterAnd(projects || []);
		}
	}, [mode, projects, searchForm.searchValue, userIds, workspaceIds, options.isDisabled]);

	function filterOr(input: typeof projects) {
		let _input = [] as Exclude<typeof projects, null | undefined>;
		if (searchForm.searchValue) {
			_input = _input.concat(filterBySearch(input));
		}
		if (userIds.length > 0) {
			_input = _input.concat(filterByUsers(input, "some"));
		}
		if (workspaceIds.length > 0) {
			_input = _input.concat(filterByWorkspaces(input, "some"));
		}
		return uniqueArrayByKey(_input, "id");
	}

	function filterAnd(input: typeof projects) {
		let _input = input;

		if (searchForm.searchValue) {
			_input = filterBySearch(_input);
		}
		if (userIds.length > 0) {
			_input = filterByUsers(_input, "every");
		}
		if (workspaceIds.length > 0) {
			_input = filterByWorkspaces(_input, "every");
		}

		return _input || [];
	}

	function filterBySearch(input: typeof projects) {
		if (!input) return [] as Exclude<typeof projects, null | undefined>;
		return filter(input, searchForm.searchValue);
	}

	function filterByUsers(input: typeof projects, fn: "some" | "every") {
		if (userIds.length === 0) return input || [];
		return (
			input?.filter((project) =>
				userIds[fn]((userId) => project._members?.map((m) => m.userId).includes(userId)),
			) || []
		);
	}

	function filterByWorkspaces(input: typeof projects, fn: "some" | "every") {
		if (workspaceIds.length === 0) return input || [];
		return (
			input?.filter((project) =>
				workspaceIds[fn]((workspaceId) => project.workspaceId === workspaceId),
			) || []
		);
	}

	function reset() {
		searchForm.setValue("searchValue", "");
		setUserIds([]);
		setWorkspaceIds([]);
	}

	const count = filtered.length;

	return {
		modeControl: modeForm.control,
		searchValue: searchForm.searchValue,
		searchFormControl: searchForm.control,
		filtered,
		userIds,
		setUserIds,
		workspaceIds,
		setWorkspaceIds,
		options,
		count,
		reset,
	};
}
